import type { CloudUser } from '../app/cloud';
import { cloud } from '../app/cloud';
import { el } from './dom';

export interface AuthDialogOptions {
  googleEnabled: boolean;
  googleCallbackURL?: string;
  /** Signed Better Auth OAuth-provider query preserved across a custom login surface. */
  oauthQuery?: string;
  initialMode?: 'sign-in' | 'sign-up';
  dismissible?: boolean;
  /** Mount directly into a page instead of creating a modal overlay. */
  container?: HTMLElement;
  signIn(email: string, password: string): Promise<CloudUser | void>;
  signUp(email: string, password: string): Promise<CloudUser | void>;
  onAuthenticated?(): boolean | void | Promise<boolean | void>;
}

/** One polished credential/OAuth surface shared by the library and editor gate. */
export function openAuthDialog(options: AuthDialogOptions): () => void {
  const inline = options.container !== undefined;
  if (!inline) document.querySelector('.auth-overlay')?.remove();

  let mode: 'sign-in' | 'sign-up' | 'recover' = options.initialMode ?? 'sign-in';
  let busy = false;
  const panel = el('section', {
    class: `auth-dialog${inline ? ' auth-inline' : ''}`,
    role: inline ? 'region' : 'dialog',
    ...(inline ? {} : { 'aria-modal': 'true' }),
    'aria-labelledby': 'auth-title',
  });
  const overlay = inline ? options.container! : el('div', { class: 'auth-overlay' }, [panel]);

  const close = (): void => (inline ? panel.remove() : overlay.remove());
  if (!inline) {
    overlay.addEventListener('pointerdown', (event) => {
      if (event.target === overlay && options.dismissible !== false && !busy) close();
    });
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && options.dismissible !== false && !busy) close();
    });
  }

  const render = (): void => {
    if (mode === 'recover') {
      const handle = el('input', {
        class: 'auth-input',
        placeholder: 'your-handle',
        autocomplete: 'username',
        required: true,
      }) as HTMLInputElement;
      const error = el('p', { class: 'auth-error', role: 'alert', 'aria-live': 'polite' });
      const recover = (kind: 'forgot-email' | 'reset-password') => {
        if (busy) return;
        busy = true;
        error.textContent = 'Requesting recovery email…';
        void cloud
          .recoverAccount(handle.value.replace(/^@/, ''), kind)
          .then((message) => {
            error.textContent = message;
          })
          .catch((reason) => {
            error.textContent = (reason as Error).message;
          })
          .finally(() => (busy = false));
      };
      const form = el('form', { class: 'auth-form auth-recovery-form' }, [
        el('label', { class: 'auth-label' }, ['Public handle', handle]),
        error,
        el(
          'button',
          {
            class: 'btn primary auth-submit',
            type: 'button',
            onclick: () => recover('forgot-email'),
          },
          ['Remind me of my email'],
        ),
        el(
          'button',
          { class: 'auth-google', type: 'button', onclick: () => recover('reset-password') },
          ['Request password reset'],
        ),
        el(
          'button',
          {
            class: 'auth-recovery-link',
            type: 'button',
            onclick: () => {
              mode = 'sign-in';
              render();
            },
          },
          ['Back to sign in'],
        ),
      ]);
      panel.replaceChildren(
        el('div', { class: 'auth-brand' }, [
          el('span', { class: 'auth-mark', 'aria-hidden': 'true' }, ['K']),
          el('span', {}, ['KilnCat']),
        ]),
        el('div', { class: 'auth-heading' }, [
          el('h1', { id: 'auth-title' }, ['Recover your account']),
          el('p', {}, ['Enter your public handle. We never reveal account email in the browser.']),
        ]),
        form,
        ...(options.dismissible !== false
          ? [el('button', { class: 'auth-close', 'aria-label': 'Close', onclick: close }, ['×'])]
          : []),
      );
      if (!inline) window.setTimeout(() => handle.focus());
      return;
    }
    const signingUp = mode === 'sign-up';
    const email = el('input', {
      class: 'auth-input',
      type: 'email',
      name: 'email',
      placeholder: 'you@example.com',
      autocomplete: 'email',
      required: true,
    }) as HTMLInputElement;
    const password = el('input', {
      class: 'auth-input',
      type: 'password',
      name: 'password',
      placeholder: 'At least 8 characters',
      autocomplete: signingUp ? 'new-password' : 'current-password',
      minlength: 8,
      required: true,
    }) as HTMLInputElement;
    const error = el('p', { class: 'auth-error', role: 'alert', 'aria-live': 'polite' });
    const submit = el('button', { class: 'btn primary auth-submit', type: 'submit' }, [
      signingUp ? 'Create account' : 'Sign in',
    ]) as HTMLButtonElement;

    const setBusy = (next: boolean): void => {
      busy = next;
      for (const control of panel.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        'input, button',
      )) {
        control.disabled = next;
      }
      submit.textContent = next
        ? signingUp
          ? 'Creating account…'
          : 'Signing in…'
        : signingUp
          ? 'Create account'
          : 'Sign in';
    };

    const credentials = (): [string, string] => {
      const address = email.value.trim();
      if (!address || !email.validity.valid) throw new Error('Enter a valid email address');
      if (password.value.length < 8) throw new Error('Password must be at least 8 characters');
      return [address, password.value];
    };

    const form = el('form', { class: 'auth-form' }, [
      el('label', { class: 'auth-label' }, ['Email', email]),
      el('label', { class: 'auth-label' }, ['Password', password]),
      error,
      submit,
    ]) as HTMLFormElement;
    form.onsubmit = (event) => {
      event.preventDefault();
      if (busy) return;
      error.textContent = '';
      void (async () => {
        try {
          const [address, secret] = credentials();
          setBusy(true);
          if (signingUp) await options.signUp(address, secret);
          else await options.signIn(address, secret);
          const opened = await options.onAuthenticated?.();
          if (opened === false) {
            error.textContent = 'This account does not have access to that texture.';
            setBusy(false);
            return;
          }
          close();
        } catch (reason) {
          error.textContent = (reason as Error).message;
          setBusy(false);
        }
      })();
    };

    const tabs = el(
      'div',
      { class: 'auth-tabs', role: 'tablist' },
      (['sign-in', 'sign-up'] as const).map((next) =>
        el(
          'button',
          {
            class: `auth-tab${mode === next ? ' on' : ''}`,
            type: 'button',
            role: 'tab',
            'aria-selected': String(mode === next),
            onclick: () => {
              if (busy || mode === next) return;
              mode = next;
              render();
            },
          },
          [next === 'sign-in' ? 'Sign in' : 'Create account'],
        ),
      ),
    );

    const children: HTMLElement[] = [
      el('div', { class: 'auth-brand' }, [
        el('span', { class: 'auth-mark', 'aria-hidden': 'true' }, ['K']),
        el('span', {}, ['KilnCat']),
      ]),
      el('div', { class: 'auth-heading' }, [
        el('h1', { id: 'auth-title' }, [signingUp ? 'Create your workspace' : 'Welcome back']),
        el('p', {}, [
          signingUp
            ? 'Design and export in free sessions, or choose Pro for cloud saves and collaboration.'
            : 'Open your account, shared textures, and cloud workspace.',
        ]),
      ]),
      tabs,
    ];

    if (options.googleEnabled) {
      children.push(
        el(
          'button',
          {
            class: 'auth-google',
            type: 'button',
            onclick: () => {
              if (busy) return;
              setBusy(true);
              void cloud
                .googleSignIn(options.googleCallbackURL ?? '/documents', options.oauthQuery)
                .catch((reason) => {
                  error.textContent = (reason as Error).message;
                  setBusy(false);
                });
            },
          },
          [el('span', { class: 'google-g', 'aria-hidden': 'true' }, ['G']), 'Continue with Google'],
        ),
        el('div', { class: 'auth-separator' }, [el('span', {}, ['or continue with email'])]),
      );
    }
    children.push(
      form,
      el(
        'button',
        {
          class: 'auth-recovery-link',
          type: 'button',
          onclick: () => {
            mode = 'recover';
            render();
          },
        },
        ['Forgot password or email?'],
      ),
      el('p', { class: 'auth-legal' }, [
        ...(signingUp
          ? ['By creating an account, you confirm you are 18 or older and agree to the ']
          : []),
        el('a', { href: '/terms', target: '_blank' }, ['Terms']),
        ' and ',
        el('a', { href: '/privacy', target: '_blank' }, ['Privacy Policy']),
        ...(signingUp ? ['.'] : []),
      ]),
    );

    if (options.dismissible !== false) {
      children.push(
        el(
          'button',
          {
            class: 'auth-close',
            type: 'button',
            'aria-label': 'Close',
            onclick: close,
          },
          ['×'],
        ),
      );
    }

    panel.replaceChildren(...children);
    if (!inline) window.setTimeout(() => email.focus());
  };

  render();
  if (inline) overlay.replaceChildren(panel);
  else document.body.appendChild(overlay);
  return close;
}
